import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ContentType } from '@app/shared/models/clux/enum';
import { Attachment, ParentType } from '@models/support/model';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs';
import { ClientQuery } from 'src/app/state';
import { v4 as uuid } from 'uuid';
import { stringFormat } from '@brycemarshall/string-format';

import { environment } from '../../../environments/environment';
import { ContentDisposition, CoreService } from '../models/clux/enum';
import { Uri } from '../utils/uri';
import { AttachmentUrlResponse, MatchType, SearchCriteria } from '@models/file/model';
import { ServiceFactory } from './service.factory';
import { CommonConstants } from '../constants/common.constant';

@Injectable({
  providedIn: 'root',
})
export class AttachmentService {
  private clientQuery = inject(ClientQuery);
  private http = inject(HttpClient);
  private serviceFactory = inject(ServiceFactory);

  private readonly supportRequestAttachmentsBucket = `${environment.name}-external-attachment`;

  public getAttachments(ids: string[]): Observable<Attachment[]> {
    const clientId = this.clientQuery.getActive().id;
    const searchCriteria: SearchCriteria = [
      {
        key: 'id',
        value: ids.join('|'),
        matchType: MatchType.IN,
      },
    ];
    return this.serviceFactory.search<Attachment[]>(CommonConstants.getPath.file.serviceKey, stringFormat(CommonConstants.getPath.file.searchAttachment, { profileId: clientId }), searchCriteria).pipe(map((res) => res.data));
  }

  public getAttachmentForDocument(documentId: string): Observable<Attachment> {
    const clientId = this.clientQuery.getActive().id;
    const searchCriteria: SearchCriteria = [
      {
        key: 'parentId',
        value: documentId,
      },
    ];
    return this.serviceFactory.search<Attachment[]>(CommonConstants.getPath.file.serviceKey, stringFormat(CommonConstants.getPath.file.searchAttachment, { profileId: clientId }), searchCriteria).pipe(map((res) => res.data[0]));
  }

  public getAttachmentContentUrl(attachmentId: string, contentDisposition: ContentDisposition = ContentDisposition.Inline, contentType?: ContentType): Observable<string | null> {
    const client = this.clientQuery.getActive();
    const url = new Uri(`/profile/${client.id}/fileAttachment/${attachmentId}/geturl`, CoreService.File);
    const httpParams: {
      fromObject: {
        [param: string]: string | ReadonlyArray<string>;
      };
    } = {
      fromObject: {
        contentDisposition: contentDisposition.toLowerCase(),
      },
    };
    if (contentType) {
      httpParams.fromObject.contentType = String(contentType);
    }
    const httpOptions = {
      params: new HttpParams(httpParams),
    };

    return this.http.get<AttachmentUrlResponse>(url.toString(), httpOptions).pipe(
      map((res) => {
        if (!res) {
          return null;
        }

        if (!res.sync) {
          // attachment archived or deleted
          return null;
        }

        if (!res.url) {
          // url not generated for some reason
          return null;
        }

        return res.url;
      })
    );
  }

  public uploadFileToS3(file: File): Observable<Attachment> {
    const attachmentId = uuid();
    const attachment: Attachment = {
      attachmentType: file.name.substr(file.name.lastIndexOf('.') + 1),
      friendlyFileName: file.name.substr(0, file.name.lastIndexOf('.')),
      filePath: `s3://${this.supportRequestAttachmentsBucket}/${attachmentId}`,
    };

    return this.getS3PutUrl(attachment, attachmentId).pipe(
      switchMap((url) => {
        const headers = this.getHeadersForS3Request(attachment);
        return this.http.put<void>(url, file, { headers });
      }),
      map(() => attachment)
    );
  }

  private getS3PutUrl(attachment: Attachment, attachmentId: string): Observable<string> {
    const client = this.clientQuery.getActive();
    const url = new Uri(`/profile/${client.id}/fileAttachment/${attachmentId}/puturl`, CoreService.File);
    const metadata = {
      fileAttachmentType: attachment.attachmentType,
      name: encodeURIComponent(attachment.friendlyFileName),
      parentId: client.id,
      parentType: ParentType.CLIENT,
    };

    return this.http.post<string>(url.toString(), metadata);
  }

  private getHeadersForS3Request(attachment: Attachment): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/octet-stream',
      'x-amz-meta-name': encodeURIComponent(attachment.friendlyFileName),
      'x-amz-meta-parentId': this.clientQuery.getActiveId(),
      'x-amz-meta-parentType': ParentType.CLIENT,
      'x-amz-tagging': `ParentType=${ParentType.CLIENT}`,
    });
  }
}
